import React from 'react';
import styled from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';
import { useSelector } from 'react-redux';

import useSmoothScrollNavigation from '@src/hooks/useSmoothScrollNavigation';
import useEvent from '@src/hooks/useEvent';
import { isEventSoldOut, getTicketStatus } from '@src/utils/event';
import { getCurrentPrice, selectCountDownForSale } from '@src/store/selectors/event';
import { CURRENCY_SYMBOL } from '@src/const/common';
import {
	TAB_OFFLINE_EVENT_INTRODUCTION,
	TAB_OFFLINE_EVENT_INFORMATION,
	TAB_OFFLINE_EVENT_CONSIDERATIONS,
	OFFLINE_EVENT_DROPDOWN_ANCHOR,
	INFO_STATUS_ON_SALE_COUNTDOWN,
} from '@src/const/event';
import { getIsPriceVisible } from '@src/utils/hardcode';

import { TabsWrapper, TabOption, ContentWrapper } from '@src/components/common/navigationStyle';

const TimeUnit = styled.span`
	font-size: 14px;
	color: #FFF;

	&:not(:first-child) {
		margin-left: 8px;
	}
`;

const TimeValue = styled.span`
	margin-right: 4px;
	font-size: 16px;
	font-weight: ${props => props.fontWeight || 500};
`;

const TAB_OPTION_LIST = [
	TAB_OFFLINE_EVENT_INTRODUCTION,
	TAB_OFFLINE_EVENT_INFORMATION,
	TAB_OFFLINE_EVENT_CONSIDERATIONS,
];

const CountDownLabel = styled.div`
	margin-right: 12px;
	color: #FFFFFF;
`;

const CountDownWrapper = styled.div`
	display: flex;
	justify-content: center;
	padding: 2px 0px 1px;
	background: ${props => props.theme.colors.secondary};
`;

const PriceWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 312px;
	padding: 8px 20px;
	background: ${props => props.theme.colors.primary};

	& > * {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	& > *:not(:first-child) {
		margin-left: 20px;
	}

	@media
		${props => props.theme.device.desktop},
		${props => props.theme.device.tablet} {
		flex: 0 0 312px;
	}

	@media
		${props => props.theme.device.smTablet},
		${props => props.theme.device.mobile} {
		display: none;
	}
`;

const Price = styled.div`
	color: #FFFFFF;
	font-size: 20px;
`;

const AddToCartButton = styled.div`
	width: 128px;
	padding: 8px 24px;
	border-radius: 4px;
	border: 1px solid #FFFFFF;
	color: #FFFFFF;
	cursor: pointer;
	opacity: ${props => (props.isDisable ? 0.5 : 1)};
`;

interface IContent {
	isFixed?: boolean;
}

const Content = ({ isFixed = false }: IContent): JSX.Element => {
	const { t } = useTranslation();
	const { event, sortedEventSlots } = useEvent();
	const currentPrice = useSelector(getCurrentPrice);
	const countdown = useSelector(selectCountDownForSale);
	const { activeTabIndex, handleClickTab } = useSmoothScrollNavigation({
		tabOptionList: TAB_OPTION_LIST,
	});
	const { id: eventId, slots } = event || {};
	const isPriceVisible = getIsPriceVisible(eventId);
	const { status, countDownTime } = getTicketStatus({ slots });
	const isSoldOut = isEventSoldOut(sortedEventSlots);
	const timerLabel = status === INFO_STATUS_ON_SALE_COUNTDOWN ?
		t('event:countdown_for_sale') :
		t('event:events_countdown');

	const handleClickScroll = () => {
		const targetEle = document.getElementById(OFFLINE_EVENT_DROPDOWN_ANCHOR);

		window.scrollTo({
			top: targetEle.offsetTop - 20,
			behavior: 'smooth',
		});
	};

	return (
		<>
			{(isFixed && countDownTime) && (
				<CountDownWrapper>
					<CountDownLabel>{timerLabel}</CountDownLabel>
					<div className="timer-content">
						<TimeUnit>
							<TimeValue>{countdown.days}</TimeValue>
							{t('time:day')}
						</TimeUnit>
						<TimeUnit>
							<TimeValue>{countdown.hours}</TimeValue>
							{t('time:hour')}
						</TimeUnit>
						<TimeUnit>
							<TimeValue>{countdown.minutes}</TimeValue>
							{t('time:minute')}
						</TimeUnit>
						<TimeUnit>
							<TimeValue>{countdown.seconds}</TimeValue>
							{t('time:second')}
						</TimeUnit>
					</div>
				</CountDownWrapper>
			)}
			<ContentWrapper>
				<TabsWrapper isFixed={isFixed} tabNumber={TAB_OPTION_LIST.length}>
					{TAB_OPTION_LIST.map((tabOptionKey, index) => {
						const isActive = index === activeTabIndex;

						return (
							<TabOption
								key={tabOptionKey}
								className="content__tab"
								onClick={() => handleClickTab(tabOptionKey)}
								isActive={isActive}
							>
								{t(`event:${tabOptionKey}`)}
							</TabOption>
						);
					})}
				</TabsWrapper>
				{isFixed && (
					<PriceWrapper>
						<Price>
							{
								isPriceVisible ?
									`${CURRENCY_SYMBOL} ${currentPrice.toLocaleString()}` :
									t('common:consultation')
							}
						</Price>
						<AddToCartButton
							isDisable={isSoldOut}
							onClick={isSoldOut ? null : handleClickScroll}
						>
							{isSoldOut ? t('event:sold_out_tag') : t('event:select_slot')}
						</AddToCartButton>
					</PriceWrapper>
				)}
			</ContentWrapper>
		</>
	);
};

export default Content;
